import { LIFECYCLE_STATUS_STYLES, REGION_OPTIONS } from '../constants/raiseQueryPage';

/**
 * Normalize lifecycle status value
 * @param {string|number} value - Status value
 * @returns {string} Normalized status
 */
export const normalizeLifecycleStatus = (value) => {
  if (!value && value !== 0) return '';
  const normalized = value.toString().trim().toLowerCase();
  if (!normalized) return '';
  return normalized === 'open' ? 'raised' : normalized;
};

/**
 * Format query type for display
 * @param {string} queryType - Query type value
 * @returns {string} Formatted query type
 */
export const formatQueryType = (queryType) => {
  if (!queryType) return 'N/A';
  return queryType
    .toString()
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

/**
 * Get lifecycle status metadata (label and color)
 * @param {string} statusLabel - Status label
 * @returns {Object|null} Status metadata or null
 */
export const getLifecycleStatusMeta = (statusLabel) => {
  if (!statusLabel) return null;
  const key = normalizeLifecycleStatus(statusLabel);
  return LIFECYCLE_STATUS_STYLES[key] || null;
};

/**
 * Get last updated timestamp from query
 * @param {Object} query - Query object
 * @returns {string|null} Timestamp or null
 */
export const getLastUpdatedTimestamp = (query) =>
  query?.updatedAt ||
  query?.lastResponseAt ||
  query?.resolvedAt ||
  query?.respondedAt ||
  query?.raisedAt ||
  query?.createdAt ||
  null;

/**
 * Normalize ID from various formats
 * @param {string|Object} value - ID value or object with _id/id
 * @returns {string|null} Normalized ID or null
 */
export const normalizeId = (value) => {
  if (!value) return null;
  if (typeof value === 'string') return value;
  return value._id || value.id || null;
};

/**
 * Check if status indicates need for clarification
 * @param {string} statusLabel - Status label
 * @param {string} status - Status
 * @param {string} normalized - Normalized status
 * @returns {boolean} True if need clarification
 */
export const isNeedClarificationStatus = (statusLabel, status, normalized) => {
  return (
    normalized === 'additional_clarification' ||
    normalized === 'need_clarification' ||
    normalized === 'clarification' ||
    normalized === 'reopened' ||
    (statusLabel && statusLabel.toLowerCase() === 'additional_clarification') ||
    (statusLabel && statusLabel.toLowerCase() === 'reopened') ||
    (status && status.toLowerCase() === 'reopened')
  );
};

/**
 * Calculate status summary from queries
 * @param {Array} queries - Array of query objects
 * @returns {Object} Status summary counts
 */
export const calculateStatusSummary = (queries) => {
  return queries.reduce(
    (acc, query) => {
      const statusLabel = query.statusLabel || '';
      const status = query.status || '';
      const normalized = normalizeLifecycleStatus(statusLabel || status);

      if (normalized === 'submitted') acc.submitted += 1;
      if (normalized === 'raised' || normalized === 'open') acc.open += 1;
      if (normalized === 'delegated') acc.delegated += 1;
      if (normalized === 'escalated') acc.escalated += 1;
      if (normalized === 'closed') acc.closed += 1;

      if (isNeedClarificationStatus(statusLabel, status, normalized)) {
        acc.needClarification += 1;
      }

      return acc;
    },
    { submitted: 0, open: 0, delegated: 0, escalated: 0, closed: 0, needClarification: 0 }
  );
};

/**
 * Calculate region status summary from queries
 * @param {Array} queries - Array of query objects
 * @param {Array} visibleRegionOptions - Visible region options
 * @returns {Object} Region status summary
 */
export const calculateRegionStatusSummary = (queries, visibleRegionOptions) => {
  const regionSummary = {};
  const regionProjects = {};

  // Initialize region summaries
  visibleRegionOptions.forEach((regionOption) => {
    regionSummary[regionOption.value] = {
      raised: 0,
      closed: 0,
      delegated: 0,
      escalated: 0,
      total: 0,
      totalProjects: 0,
      totalOpenQueries: 0,
    };
    regionProjects[regionOption.value] = new Set();
  });

  // Process queries
  queries.forEach((query) => {
    const queryRegion = (query.region || '').trim();
    if (!regionSummary[queryRegion]) {
      regionSummary[queryRegion] = {
        raised: 0,
        closed: 0,
        delegated: 0,
        escalated: 0,
        total: 0,
        totalProjects: 0,
        totalOpenQueries: 0,
      };
    }
    if (!regionProjects[queryRegion]) {
      regionProjects[queryRegion] = new Set();
    }

    // Track unique projects
    const projectName = query.projectName || '';
    if (projectName) {
      regionProjects[queryRegion].add(projectName);
    }

    const statusLabel = query.statusLabel || '';
    const status = query.status || '';
    const normalized = normalizeLifecycleStatus(statusLabel || status);

    regionSummary[queryRegion].total += 1;

    // Count open queries (not closed)
    if (normalized !== 'closed') {
      regionSummary[queryRegion].totalOpenQueries += 1;
    }

    if (normalized === 'raised' || normalized === 'open') {
      regionSummary[queryRegion].raised += 1;
    }
    if (normalized === 'closed') {
      regionSummary[queryRegion].closed += 1;
    }
    if (normalized === 'delegated') {
      regionSummary[queryRegion].delegated += 1;
    }
    if (normalized === 'escalated') {
      regionSummary[queryRegion].escalated += 1;
    }
  });

  // Set total projects count
  Object.keys(regionSummary).forEach((regionCode) => {
    regionSummary[regionCode].totalProjects = regionProjects[regionCode]?.size || 0;
  });

  return regionSummary;
};

/**
 * Count unique projects per region from a list of queries — i.e. how many
 * distinct projects have at least one query in that region. Backend counts
 * (raised/closed/etc.) don't carry project identity, so this has to be
 * derived client-side from the actual query list.
 *
 * @param {Array} queries
 * @returns {Object} region code -> unique project count
 */
export const countProjectsByRegion = (queries) => {
  const regionProjects = {};

  (queries || []).forEach((query) => {
    const region = (query.region || '').trim();
    const projectName = query.projectName || '';
    if (!region || !projectName) return;

    if (!regionProjects[region]) {
      regionProjects[region] = new Set();
    }
    regionProjects[region].add(projectName);
  });

  const counts = {};
  Object.keys(regionProjects).forEach((region) => {
    counts[region] = regionProjects[region].size;
  });
  return counts;
};

/**
 * Build regionStatusSummary from backend counts object (e.g. counts.assignedQueries).
 * The backend object has region codes as keys, each with raised/responded/closed/escalated/
 * delegated/needClarification fields.
 *
 * The output shape is the same as `calculateRegionStatusSummary` so it drops in as a
 * replacement for `setRegionStatusSummary`.
 *
 * @param {Object} backendCounts - e.g. response.data.counts.assignedQueries
 * @param {Array} [queries] - Full query list (unfiltered by region/status) used to derive
 *   `totalProjects` per region, since backend counts alone don't carry project identity.
 * @returns {Object} regionStatusSummary keyed by region code
 */
export const buildRegionStatusSummary = (backendCounts, queries = []) => {
  if (!backendCounts || typeof backendCounts !== 'object') return {};

  const projectCountsByRegion = countProjectsByRegion(queries);

  const regionSummary = {};
  Object.keys(backendCounts).forEach((regionCode) => {
    const r = backendCounts[regionCode] || {};
    regionSummary[regionCode] = {
      raised: r.raised || 0,
      closed: r.closed || 0,
      delegated: r.delegated || 0,
      escalated: r.escalated || 0,
      // Keep legacy fields so nothing else breaks
      total: (r.raised || 0) + (r.responded || 0) + (r.closed || 0) + (r.escalated || 0) + (r.delegated || 0) + (r.needClarification || 0),
      totalProjects: projectCountsByRegion[regionCode] || 0,
      totalOpenQueries: (r.raised || 0) + (r.delegated || 0) + (r.escalated || 0) + (r.needClarification || 0),
    };
  });

  return regionSummary;
};

/**
 * Derive a single flat status summary from backend counts, respecting region selection.
 * Used for status-summary cards (Raised / Delegated / Escalated / Closed).
 *
 * @param {Object} backendCounts - e.g. response.data.counts.assignedQueries
 * @param {string|null} selectedRegion - Currently selected region code, or null for all
 * @returns {{ raised: number, delegated: number, escalated: number, closed: number }}
 */
export const buildStatSummaryFromBackend = (backendCounts, selectedRegion) => {
  const empty = { raised: 0, delegated: 0, escalated: 0, closed: 0 };
  if (!backendCounts || typeof backendCounts !== 'object') return empty;

  if (selectedRegion) {
    const r = backendCounts[selectedRegion] || {};
    return {
      raised: r.raised || 0,
      delegated: r.delegated || 0,
      escalated: r.escalated || 0,
      closed: r.closed || 0,
    };
  }

  // Sum across all regions
  return Object.values(backendCounts).reduce(
    (acc, r) => {
      acc.raised += r.raised || 0;
      acc.delegated += r.delegated || 0;
      acc.escalated += r.escalated || 0;
      acc.closed += r.closed || 0;
      return acc;
    },
    { raised: 0, delegated: 0, escalated: 0, closed: 0 }
  );
};
